import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { getBlockColor, getEmissiveColor } from "./SpectrumMaterial";

const BLOCK_SIZE = 0.88;
const BLOCK_DEPTH = BLOCK_SIZE * 0.75;
const HALF_DEPTH = BLOCK_DEPTH * 0.5;

// ──────────── Shared Geometries (created once) ────────────
const sharedGemGeometry = new THREE.BoxGeometry(
  BLOCK_SIZE, BLOCK_SIZE, BLOCK_DEPTH, 2, 2, 1,
);
const sharedEdgeGeometry = new THREE.EdgesGeometry(
  new THREE.BoxGeometry(
    BLOCK_SIZE * 1.015, BLOCK_SIZE * 1.015, BLOCK_DEPTH * 1.015,
  ),
);
const sharedCoreGeometry = new THREE.OctahedronGeometry(0.19, 0);
const sharedAuraGeometry = new THREE.BoxGeometry(
  BLOCK_SIZE * 1.25, BLOCK_SIZE * 1.25, BLOCK_DEPTH * 0.35,
);
// Cross starburst rays (falling piece only)
const sharedRayGeometry = new THREE.PlaneGeometry(BLOCK_SIZE * 2.0, BLOCK_SIZE * 0.045);
const sharedSparkleRayGeometry = new THREE.PlaneGeometry(BLOCK_SIZE * 1.3, BLOCK_SIZE * 0.025);
// Rainbow refraction band
const sharedBandGeometry = new THREE.PlaneGeometry(BLOCK_SIZE * 0.92, BLOCK_SIZE * 0.065);
// Corner star (tiny 4-point diamond)
const sharedStarGeometry = new THREE.CircleGeometry(0.038, 4);

// ──────────── Color Helpers ────────────
const SECONDARY_COLORS = {
  "l-tetromino-active": "#FFD700",
  "j-tetromino-active": "#9B30FF",
  "t-tetromino-active": "#FF1493",
  "square-tetromino-active": "#FF8C00",
  "line-tetromino-active": "#32CD32",
  "z-tetromino-active": "#FF69B4",
  "s-tetromino-active": "#00FFFF",
};

const TERTIARY_COLORS = {
  "l-tetromino-active": "#FF3030",
  "j-tetromino-active": "#00FFFF",
  "t-tetromino-active": "#4169FF",
  "square-tetromino-active": "#32CD32",
  "line-tetromino-active": "#9B30FF",
  "z-tetromino-active": "#FFD700",
  "s-tetromino-active": "#FFD700",
};

const CORNER_POS = [
  [-BLOCK_SIZE * 0.38, -BLOCK_SIZE * 0.38],
  [-BLOCK_SIZE * 0.38, BLOCK_SIZE * 0.38],
  [BLOCK_SIZE * 0.38, -BLOCK_SIZE * 0.38],
  [BLOCK_SIZE * 0.38, BLOCK_SIZE * 0.38],
];

const SpectrumBlock3D = ({
  position,
  colorClass,
  isGhost = false,
  isActive = false,
  isPlaced = false,
  animate = true,
  scale = 1,
}) => {
  const groupRef = useRef();
  const coreRef = useRef();
  const auraRef = useRef();
  const edgeRef = useRef();
  const ray0Ref = useRef();
  const ray1Ref = useRef();
  const ray2Ref = useRef();
  const ray3Ref = useRef();
  const band1Ref = useRef();
  const band2Ref = useRef();
  const star0Ref = useRef();
  const star1Ref = useRef();
  const star2Ref = useRef();
  const star3Ref = useRef();
  const mainMeshRef = useRef();

  const phaseRef = useRef(Math.random() * Math.PI * 2);

  const baseColor = getBlockColor(colorClass);
  const emissiveColor = getEmissiveColor(colorClass);
  const secondaryColor = SECONDARY_COLORS[colorClass] || "#FFFFFF";
  const tertiaryColor = TERTIARY_COLORS[colorClass] || "#FFFFFF";

  const starRefs = [star0Ref, star1Ref, star2Ref, star3Ref];
  const rayRefs = [ray0Ref, ray1Ref, ray2Ref, ray3Ref];

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const p = phaseRef.current;

    // ── Active piece breathing scale ──
    if (groupRef.current && animate && isActive) {
      const breathe = Math.sin(t * 3.0) * 0.022 + 1;
      groupRef.current.scale.setScalar(breathe);
    }

    if (isGhost) return;

    // ── Spinning diamond core ──
    if (coreRef.current) {
      coreRef.current.rotation.y = t * 1.8 + p;
      coreRef.current.rotation.x = t * 0.7;
      if (coreRef.current.material) {
        const cp = isActive
          ? 0.85 + Math.sin(t * 4.0 + p) * 0.15
          : 0.7 + Math.sin(t * 2.0 + p) * 0.15;
        coreRef.current.material.opacity = cp;
        coreRef.current.material.emissiveIntensity = cp * 2.8;
      }
    }

    // ── Outer aura pulse ──
    if (auraRef.current?.material) {
      const ap = isActive
        ? 0.4 + Math.sin(t * 2.5 + p) * 0.15
        : 0.3 + Math.sin(t * 1.3 + p) * 0.08;
      auraRef.current.material.opacity = ap;
    }

    // ── Edge glow ──
    if (edgeRef.current?.material) {
      const ep = isActive
        ? 0.85 + Math.sin(t * 5.0 + p) * 0.15
        : 0.7 + Math.sin(t * 2.0 + p) * 0.12;
      edgeRef.current.material.opacity = ep;
    }

    // ── Starburst rays (falling piece only) ──
    if (!isPlaced) {
      const rayBaseOpacity = isActive
        ? 0.55 + Math.sin(t * 3.0 + p) * 0.3
        : 0.08 + Math.sin(t * 1.5 + p) * 0.06;
      const rayScale = isActive ? 1.0 + Math.sin(t * 3.0 + p) * 0.15 : 0.6;

      rayRefs.forEach((ref, i) => {
        if (ref.current) {
          const stagger = i * 0.4;
          const opacity =
            rayBaseOpacity * (0.85 + Math.sin(t * 5 + p + stagger) * 0.15);
          ref.current.scale.set(rayScale, 1, 1);
          if (ref.current.material) {
            ref.current.material.opacity = Math.max(0, opacity);
          }
        }
      });
    }

    // ── Rainbow refraction bands ──
    if (band1Ref.current) {
      band1Ref.current.position.y = Math.sin(t * 1.2 + p) * BLOCK_SIZE * 0.22;
      if (band1Ref.current.material) {
        const b1 = isActive
          ? 0.55 + Math.sin(t * 3.0 + p) * 0.2
          : 0.4 + Math.sin(t * 1.5 + p) * 0.12;
        band1Ref.current.material.opacity = b1;
      }
    }
    if (band2Ref.current) {
      band2Ref.current.position.y =
        Math.sin(t * 1.2 + p + 2.0) * BLOCK_SIZE * 0.18;
      if (band2Ref.current.material) {
        const b2 = isActive
          ? 0.4 + Math.sin(t * 3.5 + p + 1.0) * 0.2
          : 0.3 + Math.sin(t * 1.8 + p + 1.0) * 0.1;
        band2Ref.current.material.opacity = b2;
      }
    }

    // ── Corner star twinkle ──
    starRefs.forEach((ref, i) => {
      if (ref.current) {
        const twinkle = Math.sin(t * 7.0 + p + i * 1.57);
        const tw = twinkle * 0.5 + 0.5;
        const starOpacity = isActive ? tw * 0.85 : tw * 0.6;
        ref.current.rotation.z = t * 4.0 + i * 0.785;
        if (ref.current.material) {
          ref.current.material.opacity = starOpacity;
        }
        const starScale = isActive ? 0.8 + tw * 0.6 : 0.7 + tw * 0.4;
        ref.current.scale.setScalar(starScale);
      }
    });

    // ── Shimmer sweep — periodic emissive flash on main material ──
    // Like CSS button ::before sweep: a brief wash of brightness across the surface.
    // No extra geometry — just a quick emissive intensity boost on the block itself.
    if (mainMeshRef.current?.material) {
      const cycleDuration = 4.0; // seconds per full cycle
      const shimmerPortion = 0.2; // shimmer lasts 20% of cycle, rest is normal
      const rawCycle = ((t + p * 2.0) % cycleDuration) / cycleDuration;

      const baseEmissive = isActive ? 1.2 : 1.0;

      if (rawCycle < shimmerPortion) {
        // Shimmer phase — quick bell curve brightness boost
        const progress = rawCycle / shimmerPortion; // 0→1
        // Smooth bell: sin curve peaks at 0.5
        const shimmer = Math.sin(progress * Math.PI);
        const boost = shimmer * 0.6; // peak boost amount
        mainMeshRef.current.material.emissiveIntensity = baseEmissive + boost;
      } else {
        mainMeshRef.current.material.emissiveIntensity = baseEmissive;
      }
    }
  });

  // ──────────── Materials ────────────
  const material = useMemo(() => {
    if (isGhost) {
      return new THREE.MeshStandardMaterial({
        color: new THREE.Color(baseColor),
        emissive: new THREE.Color(baseColor),
        emissiveIntensity: 0.4,
        metalness: 0.35,
        roughness: 0.2,
        opacity: 0.25,
        transparent: true,
        side: THREE.DoubleSide,
      });
    }

    return new THREE.MeshStandardMaterial({
      color: new THREE.Color(baseColor),
      emissive: new THREE.Color(emissiveColor),
      emissiveIntensity: isActive ? 1.2 : 1.0,
      metalness: 0.82,
      roughness: 0.05,
      side: THREE.DoubleSide,
    });
  }, [baseColor, emissiveColor, isGhost, isActive]);

  return (
    <group position={position} ref={groupRef} scale={scale}>
      {/* ═══════ MAIN GEM BODY ═══════ */}
      <mesh
        ref={mainMeshRef}
        geometry={sharedGemGeometry}
        material={material}
        castShadow={!isGhost}
        receiveShadow={!isGhost}
      />

      {/* ═══════ SPINNING DIAMOND CORE ═══════ */}
      {!isGhost && (
        <mesh ref={coreRef} geometry={sharedCoreGeometry}>
          <meshStandardMaterial
            color={baseColor}
            emissive={baseColor}
            emissiveIntensity={2.2}
            transparent
            opacity={0.75}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      )}

      {/* ═══════ OUTER AURA ═══════ */}
      {!isGhost && (
        <mesh ref={auraRef} geometry={sharedAuraGeometry}>
          <meshBasicMaterial
            color={baseColor}
            transparent
            opacity={0.22}
            side={THREE.BackSide}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      )}

      {/* ═══════ 4-POINT STARBURST RAYS (falling piece only) ═══════ */}
      {!isGhost && !isPlaced && (
        <>
          <mesh ref={ray0Ref} position={[0, 0, HALF_DEPTH + 0.01]} geometry={sharedRayGeometry}>
            <meshBasicMaterial color={baseColor} transparent opacity={0.3} blending={THREE.AdditiveBlending} depthWrite={false} />
          </mesh>
          <mesh ref={ray1Ref} position={[0, 0, HALF_DEPTH + 0.01]} rotation={[0, 0, Math.PI / 2]} geometry={sharedRayGeometry}>
            <meshBasicMaterial color={baseColor} transparent opacity={0.25} blending={THREE.AdditiveBlending} depthWrite={false} />
          </mesh>
          <mesh ref={ray2Ref} position={[0, 0, HALF_DEPTH + 0.01]} rotation={[0, 0, Math.PI / 4]} geometry={sharedSparkleRayGeometry}>
            <meshBasicMaterial color="#FFFFFF" transparent opacity={0.15} blending={THREE.AdditiveBlending} depthWrite={false} />
          </mesh>
          <mesh ref={ray3Ref} position={[0, 0, HALF_DEPTH + 0.01]} rotation={[0, 0, -Math.PI / 4]} geometry={sharedSparkleRayGeometry}>
            <meshBasicMaterial color="#FFFFFF" transparent opacity={0.15} blending={THREE.AdditiveBlending} depthWrite={false} />
          </mesh>
        </>
      )}

      {/* ═══════ RAINBOW REFRACTION BANDS ═══════ */}
      {!isGhost && (
        <>
          <mesh ref={band1Ref} position={[0, 0, HALF_DEPTH + 0.02]} geometry={sharedBandGeometry}>
            <meshBasicMaterial color={secondaryColor} transparent opacity={0.35} blending={THREE.AdditiveBlending} depthWrite={false} />
          </mesh>
          <mesh ref={band2Ref} position={[0, 0, HALF_DEPTH + 0.02]} geometry={sharedBandGeometry}>
            <meshBasicMaterial color={tertiaryColor} transparent opacity={0.25} blending={THREE.AdditiveBlending} depthWrite={false} />
          </mesh>
        </>
      )}

      {/* ═══════ CORNER STAR SPARKLES ═══════ */}
      {!isGhost &&
        CORNER_POS.map((pos, i) => (
          <mesh
            key={i}
            ref={starRefs[i]}
            position={[pos[0], pos[1], HALF_DEPTH + 0.01]}
            geometry={sharedStarGeometry}
          >
            <meshBasicMaterial
              color="#FFFFFF"
              transparent
              opacity={0.5}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
        ))}

      {/* ═══════ NEON EDGE OUTLINE ═══════ */}
      {!isGhost && (
        <lineSegments ref={edgeRef} geometry={sharedEdgeGeometry}>
          <lineBasicMaterial
            color={baseColor}
            linewidth={2}
            opacity={0.7}
            transparent
            blending={THREE.AdditiveBlending}
          />
        </lineSegments>
      )}
    </group>
  );
};

export default SpectrumBlock3D;
